import { Select } from '@lib/components'
import { IconWorld } from '@lib/icons'
import { Button } from '@octopus-do/octopus-storybook'
import { OctopusPic } from '@octopus-do/octopus-storybook/icons'
import { SETUP_SUCCESS } from '@storeOld/actions'
import type React from 'react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import API from '@/api'
import countries from '@/constants/countries.json'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { serialize } from '@/helpers'
import { useForm } from '@/hooks'
import type { AppState, AuthState, ModalProps } from '@/types'
import css from './SetupAccount.module.scss'

export const SetupAccount: React.FC<ModalProps> = () => {
  const [isLoading, setLoading] = useState(false)

  const companyNameRef = useRef<HTMLInputElement>(null)

  const dispatch = useDispatch()
  const { firstName, lastName, country } = useSelector<AppState, AuthState>(
    (state) => state.auth
  )

  const { form, errors, register, onSubmit, setForm } = useForm()

  useEffect(() => {
    if (country) {
      setForm({
        ...form,
        country,
      })
    }
  }, [])

  const handleSubmit = async (e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (isLoading) {
      return
    }

    setLoading(true)

    const payload = await API.Auth.update({
      ...serialize(e.target),
      country: form.country,
    })

    // Profile form completed (first name, last name and country all filled —
    // the Continue button stays disabled otherwise) and the update succeeded.
    sendGtmEvent(
      GtmTriggerName.AUTH_SETUP_COMPLETE,
      'Completed the account setup form and clicked Continue'
    )

    dispatch({
      type: SETUP_SUCCESS,
      payload: {
        ...payload,
        country: form.country,
      },
    })
  }

  const disabledBtn = !form.firstName || !form.lastName || !form.country

  return (
    <div className={css.container}>
      <div className={css.header}>
        <OctopusPic />

        <h5 className={css.title}>Set up your account</h5>
      </div>

      <div className={css.body}>
        <form
          onSubmit={(e: ChangeEvent<HTMLFormElement>) => {
            onSubmit(e, handleSubmit)
          }}
        >
          <div className={css.inputGroup}>
            <div className={css.inputWrap}>
              <input
                className="Clipboard-input"
                name="firstName"
                type="text"
                placeholder="First name"
                {...register('firstName', firstName, { required: true })}
              />
              {errors.firstName && (
                <div className={css.error}>{errors.firstName}</div>
              )}
            </div>

            <div className={css.inputWrap}>
              <input
                className="Clipboard-input"
                name="lastName"
                type="text"
                placeholder="Surname"
                {...register('lastName', lastName, { required: true })}
              />
              {errors.lastName && (
                <div className={css.error}>{errors.lastName}</div>
              )}
            </div>
          </div>

          <div className={css.inputWrap}>
            <Select
              height={180}
              placeholder="Country"
              type="input"
              icon={<IconWorld size={16} />}
              options={[
                ...countries.map((item) => ({
                  label: item.name,
                  value: item.name,
                })),
              ]}
              successIcon
              selected={form.country || ''}
              withSearch
              onChange={(value) => {
                setForm({
                  ...form,
                  country: value as string,
                })

                // Country is the last field the user has to pick, so hand the
                // caret to Company name instead of dropping focus on the body
                // when the select closes.
                companyNameRef.current?.focus()
              }}
            />

            {errors.country && (
              <div className={css.error}>{errors.country}</div>
            )}
          </div>

          <div className={css.inputWrap}>
            <input
              ref={companyNameRef}
              className="Clipboard-input"
              name="companyName"
              type="text"
              placeholder="Company name"
              {...register('companyName', '')}
            />
          </div>

          <Button
            className={css.button}
            type="submit"
            label="Continue"
            loading={isLoading}
            disabled={disabledBtn}
          />
        </form>
      </div>
    </div>
  )
}
